import { ToyyibPayBillRequest } from '../types';

export const TOYYIBPAY_CONFIG = {
  merchantCategory: "Persatuan Silat (ROS Registered)",
  rosRegistrationNo: "PPM-007-02-28082022",
  secretKey: import.meta.env.VITE_TOYYIBPAY_SECRET_KEY || "w3gu2gf4-o2vg-lp95-jbic-aor35d2w8c2t",
  categoryCode: import.meta.env.VITE_TOYYIBPAY_CATEGORY_CODE || "htnqvub2",
  baseUrl: import.meta.env.VITE_TOYYIBPAY_BASE_URL || "https://dev.toyyibpay.com",
};

/**
 * Creates a payment bill link for ToyyibPay FPX gateway.
 * Performs real ToyyibPay API call directly or via proxy.
 */
export async function createToyyibPayBill(payload: ToyyibPayBillRequest): Promise<{ billCode: string; paymentUrl: string }> {
  // Sanitize input payload according to ToyyibPay API limits
  const cleanBillName = (payload.billName || 'Invois PSSASMM').trim().slice(0, 30);
  const cleanBillDesc = (payload.billDescription || 'Pembayaran Yuran ASMM').trim().slice(0, 100);
  const cleanPhone = (payload.billPayorPhone || '0194589201').replace(/\D/g, '') || '0194589201';
  const cleanEmail = (payload.billPayorEmail || 'ahli@asmm.org.my').trim();
  const cleanName = (payload.billPayorName || 'Pesilat ASMM').trim();
  const centsAmount = String(Math.round((Number(payload.billAmount) || 0) * 100));

  const formData = new URLSearchParams();
  formData.append('userSecretKey', TOYYIBPAY_CONFIG.secretKey);
  formData.append('categoryCode', TOYYIBPAY_CONFIG.categoryCode);
  formData.append('billName', cleanBillName);
  formData.append('billDescription', cleanBillDesc);
  formData.append('billPriceSetting', '1');
  const returnUrl = payload.billReturnUrl || (typeof window !== 'undefined' ? `${window.location.origin}/payments` : 'https://asmm.pages.dev/payments');
  formData.append('billReturnUrl', returnUrl);
  formData.append('billCallbackUrl', 'https://asmm.pages.dev/api/toyyibpay-callback');
  formData.append('billExternalReferenceNo', `ASMM-${Date.now()}`);
  formData.append('billTo', cleanName);
  formData.append('billEmail', cleanEmail);
  formData.append('billPhone', cleanPhone);


  // 1. Cloudflare Pages Function proxy (/api/toyyibpay) — No CORS issues on production
  try {
    const resProxy = await fetch('/api/toyyibpay', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        billName: cleanBillName,
        billDescription: cleanBillDesc,
        billPayorPhone: cleanPhone,
        secretKey: TOYYIBPAY_CONFIG.secretKey,
        categoryCode: TOYYIBPAY_CONFIG.categoryCode,
        billReturnUrl: returnUrl,
      }),
    });

    if (resProxy.ok) {
      const data = await resProxy.json();
      if (data.billCode) {
        return {
          billCode: data.billCode,
          paymentUrl: `${TOYYIBPAY_CONFIG.baseUrl}/${data.billCode}`,
        };
      }
    }
  } catch {
    // Fallthrough to direct / CORS proxy
  }

  // 2. Direct ToyyibPay API POST call
  try {
    const res = await fetch(`${TOYYIBPAY_CONFIG.baseUrl}/index.php/api/createBill`, {
      method: 'POST',
      body: formData,
    });

    if (res.ok) {
      const text = await res.text();
      try {
        const data = JSON.parse(text.trim());
        if (Array.isArray(data) && data[0]?.BillCode) {
          const billCode = data[0].BillCode;
          return {
            billCode,
            paymentUrl: `${TOYYIBPAY_CONFIG.baseUrl}/${billCode}`,
          };
        }
      } catch (e) {
        console.warn("ToyyibPay API non-JSON response:", text);
      }
    }
  } catch (err) {
    console.warn("Direct ToyyibPay API call notice:", err);
  }

  // 3. Robust CORS Proxy fallback (for local development or CORS restricted browsers)
  try {
    const corsUrl = `https://corsproxy.io/?${encodeURIComponent(`${TOYYIBPAY_CONFIG.baseUrl}/index.php/api/createBill`)}`;
    const resCors = await fetch(corsUrl, {
      method: 'POST',
      body: formData,
    });

    if (resCors.ok) {
      const text = await resCors.text();
      const data = JSON.parse(text.trim());
      if (Array.isArray(data) && data[0]?.BillCode) {
        const billCode = data[0].BillCode;
        return {
          billCode,
          paymentUrl: `${TOYYIBPAY_CONFIG.baseUrl}/${billCode}`,
        };
      }
    }
  } catch {
    // Fallthrough
  }

  throw new Error("Gagal menjana pautan bayaran ToyyibPay FPX. Sila pastikan sambungan internet stabil atau gunakan kaedah bayaran Bank Transfer / QR.");
}


export interface ToyyibPayVerificationResult {
  billCode: string;
  status: 'Disahkan' | 'Menunggu Pengesahan' | 'Gagal';
  paymentDate?: string;
  amount?: number;
  message: string;
}

/**
 * Verifies transaction bill status directly via ToyyibPay API.
 * Status values: 1 = Paid (Disahkan), 2 = Pending (Menunggu), 3 = Unpaid/Failed (Gagal).
 */
export async function verifyToyyibPayBillStatus(billCode: string): Promise<ToyyibPayVerificationResult> {
  // 1. Direct ToyyibPay API status query
  try {
    const formData = new URLSearchParams();
    formData.append('billCode', billCode);
    formData.append('userSecretKey', TOYYIBPAY_CONFIG.secretKey);

    const res = await fetch(`${TOYYIBPAY_CONFIG.baseUrl}/index.php/api/getBillTransactions`, {
      method: 'POST',
      body: formData,
    });

    if (res.ok) {
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        const item = Array.isArray(data) ? data[0] : data;
        const paymentStatus = item?.billPaymentStatus;

        if (paymentStatus === '1' || paymentStatus === 1) {
          return {
            billCode,
            status: 'Disahkan',
            paymentDate: item.billPaymentDate || new Date().toISOString(),
            amount: parseFloat(item.billPaymentAmount || '0'),
            message: 'Transaksi FPX disahkan lulus oleh ToyyibPay Gateway.'
          };
        } else if (paymentStatus === '2' || paymentStatus === 2) {
          return {
            billCode,
            status: 'Menunggu Pengesahan',
            message: 'Transaksi masih dalam proses pengesahan FPX bank.'
          };
        } else if (paymentStatus === '3' || paymentStatus === 3) {
          return {
            billCode,
            status: 'Gagal',
            message: 'Transaksi FPX dibatalkan atau gagal oleh perbankan.'
          };
        }
      } catch {
        // Non-JSON or No data found
      }
    }
  } catch {
    // Fallthrough
  }

  // 2. Cloudflare Pages Function proxy
  try {
    const res = await fetch(`/api/toyyibpay-verify?billCode=${billCode}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (res.ok) {
      const data = await res.json();
      const item = Array.isArray(data) ? data[0] : data;
      const paymentStatus = item?.billPaymentStatus;
      if (paymentStatus === '1' || paymentStatus === 1) {
        return {
          billCode,
          status: 'Disahkan',
          paymentDate: item.billPaymentDate || new Date().toISOString(),
          amount: parseFloat(item.billPaymentAmount || '0'),
          message: 'Transaksi FPX disahkan lulus oleh ToyyibPay Gateway.'
        };
      } else if (paymentStatus === '2' || paymentStatus === 2) {
        return {
          billCode,
          status: 'Menunggu Pengesahan',
          message: 'Transaksi masih dalam proses pengesahan FPX bank.'
        };
      }
    }
  } catch {
    // Fallthrough
  }

  return {
    billCode,
    status: 'Menunggu Pengesahan',
    message: 'Transaksi sedang diproses di portal ToyyibPay FPX.'
  };
}
